import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Check, X, Filter } from 'lucide-react';

type Brand = {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
};

type Machine = {
  id: string;
  airportId?: string;
  name: string;
  slug: string;
  venueType: string;
  venueName?: string;
  status?: string;
  availabilityType?: string;
  location?: string;
  isActive: boolean;
};

export default function BrandMachineAssignment() {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  const [brand, setBrand] = useState<Brand | null>(null);
  const [allMachines, setAllMachines] = useState<Machine[]>([]);
  const [assignedMachineIds, setAssignedMachineIds] = useState<Set<string>>(new Set());
  const [initialAssignedIds, setInitialAssignedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVenueType, setFilterVenueType] = useState<string>('all');
  const [filterVenueName, setFilterVenueName] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showAssignedOnly, setShowAssignedOnly] = useState(false);

  useEffect(() => {
    loadData();
  }, [brandId]);

  async function loadData() {
    if (!brandId) return;

    try {
      setLoading(true);

      // Load brand
      const brandRes = await fetch(`/api/admin/brands/${brandId}`, {
        credentials: 'include',
      });
      if (brandRes.ok) {
        setBrand(await brandRes.json());
      }

      // Load all machines directly (venue-type based)
      const machinesRes = await fetch('/api/store/machines', {
        credentials: 'include',
      });
      if (machinesRes.ok) {
        const allMachinesData = await machinesRes.json();
        setAllMachines(allMachinesData);
      }

      // Load assigned machines
      const assignedRes = await fetch(`/api/admin/brands/${brandId}/machines`, {
        credentials: 'include',
      });
      if (assignedRes.ok) {
        const assignedData: Machine[] = await assignedRes.json();
        const ids = new Set<string>(assignedData.map((m) => m.id));
        setAssignedMachineIds(ids);
        setInitialAssignedIds(ids);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!brandId) return;

    try {
      setSaving(true);
      const res = await fetch(`/api/admin/brands/${brandId}/machines`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ machineIds: Array.from(assignedMachineIds) }),
      });

      if (res.ok) {
        setInitialAssignedIds(new Set(assignedMachineIds));
        alert('Machine assignments saved successfully');
      } else {
        const error = await res.json();
        alert(error.message || 'Failed to save assignments');
      }
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save assignments');
    } finally {
      setSaving(false);
    }
  }

  function toggleMachine(machineId: string) {
    const newSet = new Set(assignedMachineIds);
    if (newSet.has(machineId)) {
      newSet.delete(machineId);
    } else {
      newSet.add(machineId);
    }
    setAssignedMachineIds(newSet);
  }

  function selectAll(machines: Machine[]) {
    const newSet = new Set(assignedMachineIds);
    machines.forEach((m) => newSet.add(m.id));
    setAssignedMachineIds(newSet);
  }

  function deselectAll(machines: Machine[]) {
    const newSet = new Set(assignedMachineIds);
    machines.forEach((m) => newSet.delete(m.id));
    setAssignedMachineIds(newSet);
  }

  const filteredMachines = useMemo(() => {
    let result = allMachines;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((m) =>
        m.name.toLowerCase().includes(term) ||
        m.slug.toLowerCase().includes(term) ||
        m.location?.toLowerCase().includes(term) ||
        m.venueName?.toLowerCase().includes(term)
      );
    }

    if (filterVenueType !== 'all') {
      result = result.filter((m) => m.venueType === filterVenueType);
    }

    if (filterVenueName !== 'all') {
      result = result.filter((m) => m.venueName === filterVenueName);
    }

    if (filterStatus !== 'all') {
      result = result.filter((m) => m.status === filterStatus);
    }

    if (showAssignedOnly) {
      result = result.filter((m) => assignedMachineIds.has(m.id));
    }

    return result;
  }, [allMachines, searchTerm, filterVenueType, filterVenueName, filterStatus, showAssignedOnly, assignedMachineIds]);

  const machinesByVenueType = useMemo(() => {
    const grouped = new Map<string, Map<string, Machine[]>>();
    filteredMachines.forEach((machine) => {
      const venueType = machine.venueType || 'Uncategorized';
      const venueName = machine.venueName || 'Unknown Venue';
      
      if (!grouped.has(venueType)) {
        grouped.set(venueType, new Map());
      }
      
      const venueGroup = grouped.get(venueType)!;
      if (!venueGroup.has(venueName)) {
        venueGroup.set(venueName, []);
      }
      
      venueGroup.get(venueName)!.push(machine);
    });
    return grouped;
  }, [filteredMachines]);

  const venueTypes = useMemo(() => {
    const types = new Set(allMachines.map((m) => m.venueType).filter((v): v is string => !!v));
    return Array.from(types).sort();
  }, [allMachines]);

  const venueNames = useMemo(() => {
    const names = new Set(allMachines.map((m) => m.venueName).filter((v): v is string => !!v));
    return Array.from(names).sort();
  }, [allMachines]);

  const statusOptions = useMemo(() => {
    const statuses = new Set(allMachines.map((m) => m.status).filter((s): s is string => !!s));
    return Array.from(statuses);
  }, [allMachines]);

  const hasChanges = useMemo(() => {
    if (assignedMachineIds.size !== initialAssignedIds.size) return true;
    const assignedArray = Array.from(assignedMachineIds);
    for (let i = 0; i < assignedArray.length; i++) {
      if (!initialAssignedIds.has(assignedArray[i])) return true;
    }
    return false;
  }, [assignedMachineIds, initialAssignedIds]);

  if (loading) {
    return (
      <div className="h-full bg-slate-50">
        <div className="w-full px-2 md:px-4 py-4 md:py-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="h-full bg-slate-50">
        <div className="w-full px-2 md:px-4 py-4 md:py-8">
          <div className="text-center text-red-600">Brand not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50">
      <div className="w-full px-2 md:px-4 py-4 md:py-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/brands')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Brands
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Assign Machines to {brand.name}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {assignedMachineIds.size} of {allMachines.length} machines assigned
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAssignedMachineIds(new Set(initialAssignedIds))} disabled={!hasChanges}>
                Reset
              </Button>
              <Button onClick={handleSave} disabled={saving || !hasChanges}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Filter className="w-4 h-4" />
            Filters
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Input
                placeholder="Search machines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Select value={filterVenueType} onValueChange={setFilterVenueType}>
                <SelectTrigger>
                  <SelectValue placeholder="All venue types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All venue types</SelectItem>
                  {venueTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={filterVenueName} onValueChange={setFilterVenueName}>
                <SelectTrigger>
                  <SelectValue placeholder="All venues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All venues</SelectItem>
                  {venueNames.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showAssigned"
              checked={showAssignedOnly}
              onChange={(e) => setShowAssignedOnly(e.target.checked)}
            />
            <label htmlFor="showAssigned" className="text-sm text-slate-600">
              Show assigned only
            </label>
          </div>
        </div>

        {/* Machines List */}
        <div className="space-y-6">
          {Array.from(machinesByVenueType.entries()).map(([venueType, venueGroups]) => {
            // Calculate total assigned for this venue type
            const allMachinesInType: Machine[] = [];
            venueGroups.forEach((machines) => allMachinesInType.push(...machines));
            const assignedCountType = allMachinesInType.filter((m) => assignedMachineIds.has(m.id)).length;

            return (
              <div key={venueType} className="space-y-4">
                <div className="bg-slate-100 rounded-lg p-3">
                  <h2 className="text-xl font-bold text-slate-800">{venueType}</h2>
                  <p className="text-xs text-slate-600">
                    {assignedCountType} of {allMachinesInType.length} assigned
                  </p>
                </div>

                {Array.from(venueGroups.entries()).map(([venueName, machines]) => {
                  const assignedCount = machines.filter((m) => assignedMachineIds.has(m.id)).length;

                  return (
                    <div key={`${venueType}-${venueName}`} className="bg-white rounded-lg border border-slate-200 shadow-sm ml-4">
                      <div className="flex items-center justify-between p-4 border-b border-slate-200">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">{venueName}</h3>
                          <p className="text-xs text-slate-500">
                            {assignedCount} of {machines.length} assigned
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => selectAll(machines)}>
                            Select All
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => deselectAll(machines)}>
                            Deselect All
                          </Button>
                        </div>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {machines.map((machine) => {
                          const isAssigned = assignedMachineIds.has(machine.id);
                          return (
                            <div
                              key={machine.id}
                              className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                                isAssigned ? 'bg-blue-50' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => toggleMachine(machine.id)}
                            >
                              <div className="flex items-center gap-4 flex-1">
                                <div
                                  className={`flex items-center justify-center w-6 h-6 rounded border-2 transition-colors ${
                                    isAssigned
                                      ? 'bg-blue-600 border-blue-600'
                                      : 'border-slate-300 bg-white'
                                  }`}
                                >
                                  {isAssigned && <Check className="w-4 h-4 text-white" />}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-medium text-slate-900">{machine.name}</h4>
                                    {machine.status && (
                                      <span
                                        className={`text-xs px-2 py-0.5 rounded-full ${
                                          machine.status === 'Live/Available'
                                            ? 'bg-emerald-100 text-emerald-700'
                                            : machine.status === 'Reserved'
                                            ? 'bg-amber-100 text-amber-700'
                                            : 'bg-blue-100 text-blue-700'
                                        }`}
                                      >
                                        {machine.status}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                                    {machine.location && <span>{machine.location}</span>}
                                    {machine.availabilityType && <span>{machine.availabilityType}</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {filteredMachines.length === 0 && (
            <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
              <p className="text-slate-500">No machines found matching the current filters.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

